import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { PageTitle, Panel, Btn, Sel } from "@/admin/ui";

const TEXTS = [["hero_intro", "Landing introduction"], ["promo_message", "Promotional message (optional)"], ["about_method", "Method description"], ["disclaimer", "Disclaimer"], ["privacy", "Privacy Policy"], ["terms", "Terms & Conditions"], ["refund", "Refund & Cancellation Policy"], ["trademark_notice", "Trademark notice"], ["sample_report_note", "Sample report note"]];

export default function ContentEditor() {
  const [c, setC] = useState(null);
  const qc = useQueryClient();
  useEffect(() => {
    let live = true;
    api.get("/admin/content").then((r) => live && setC((cur) => cur || r.data));
    return () => { live = false; };
  }, []);
  if (!c) return null;
  const set = (k, v) => setC({ ...c, [k]: v });
  const setPrice = (i, k, v) => set("prices", c.prices.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const setFaq = (i, k, v) => set("faqs", c.faqs.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const save = async () => {
    try { setC((await api.put("/admin/content", { ...c, prices: c.prices.map((p) => ({ ...p, amount: Number(p.amount) })) })).data); qc.invalidateQueries({ queryKey: ["content"] }); toast.success("Website content saved — live on the website now"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <PageTitle title="Website Content" sub="Every change is versioned in the content history and recorded in the audit log.">
        <Btn onClick={save} data-testid="content-save-btn">Save changes</Btn>
      </PageTitle>
      <Panel title="Prices & currencies" testId="prices-panel">
        {c.prices.map((p, i) => (
          <div key={i} className="mb-2 flex flex-wrap items-center gap-2">
            <Input value={p.currency} onChange={(e) => setPrice(i, "currency", e.target.value.toUpperCase())} className="w-24" data-testid={`price-currency-${i}`} />
            <Input type="number" value={p.amount} onChange={(e) => setPrice(i, "amount", e.target.value)} className="w-32" data-testid={`price-amount-${i}`} />
            <Sel value={p.provider} onChange={(v) => setPrice(i, "provider", v)} options={["razorpay", "paypal"]} />
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={p.active} onChange={(e) => setPrice(i, "active", e.target.checked)} /> active</label>
            <button onClick={() => set("prices", c.prices.filter((_, j) => j !== i))} className="text-muted-foreground"><X className="h-4 w-4" /></button>
          </div>
        ))}
        <Btn variant="ghost" onClick={() => set("prices", [...c.prices, { currency: "GBP", amount: 15, provider: "paypal", active: false }])}><Plus className="h-4 w-4" /> Add currency</Btn>
        <p className="mt-2 text-xs text-muted-foreground" data-testid="prices-note">Click "Save changes" to apply. New prices take effect immediately for new orders; orders already placed keep the price they were created with. Razorpay is used for INR, PayPal for international currencies. Discounts are managed under Promo Codes.</p>
      </Panel>
      <Panel title="FAQs" testId="faqs-panel">
        {c.faqs.map((f, i) => (
          <div key={i} className="mb-3 grid gap-2 border-b border-border pb-3">
            <div className="flex gap-2"><Input value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} /><button onClick={() => set("faqs", c.faqs.filter((_, j) => j !== i))} className="text-muted-foreground"><X className="h-4 w-4" /></button></div>
            <Textarea rows={2} value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} />
          </div>
        ))}
        <Btn variant="ghost" onClick={() => set("faqs", [...c.faqs, { q: "", a: "" }])} data-testid="add-faq-btn"><Plus className="h-4 w-4" /> Add FAQ</Btn>
      </Panel>
      {TEXTS.map(([k, l]) => (
        <Panel key={k} title={l}>
          <Textarea rows={k.length > 8 ? 5 : 3} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`content-${k}-input`} />
        </Panel>
      ))}
    </div>
  );
}
